<template>
  <div class="mass-message">
    <div class="section-header">
      <h3 class="section-title">
        <i class="el-icon-message-solid"></i>
        群发消息
      </h3>
    </div>

    <div class="content-wrapper">
      <!-- 消息内容区 -->
      <div class="message-section">
        <div class="section-label">消息内容</div>
        <el-input
          type="textarea"
          v-model="messageContent"
          placeholder="请输入要发送的文字内容..."
          :rows="4"
          maxlength="1024"
          show-word-limit
        ></el-input>
      </div>

      <!-- 图片上传区 -->
      <div class="image-section">
        <div class="section-label">
          <span>添加图片</span>
          <span class="image-hint">（最多9张）</span>
        </div>
        <div class="image-list">
          <div v-for="(img, idx) in images" :key="idx" class="image-item">
            <el-image :src="getImageUrl(img)" fit="cover" class="preview-img">
              <div slot="error" class="image-error">
                <i class="el-icon-picture-outline"></i>
              </div>
            </el-image>
            <div class="remove-img" @click="onRemoveImage(idx)">
              <i class="el-icon-close"></i>
            </div>
          </div>
          <div v-if="images.length < 9" class="add-image-btn" @click="onChooseImage">
            <i class="el-icon-plus"></i>
          </div>
        </div>
      </div>

      <!-- 发送范围 -->
      <div class="range-section">
        <div class="section-label">发送范围</div>

        <!-- 选择标签 -->
        <div class="row-item">
          <div class="row-header">
            <span class="row-label">选择标签</span>
            <span class="add-btn" @click="onSelectTags">
              <i class="el-icon-plus"></i> 添加
            </span>
          </div>
          <div v-if="selectedTags.length > 0" class="selected-tags">
            <div v-for="tag in selectedTags" :key="tag.id" class="tag-chip">
              <span>{{ tag.name }}</span>
              <i class="el-icon-close" @click="onRemoveTag(tag.id)"></i>
            </div>
          </div>
          <div v-else class="empty-tip-row">
            <span>未选择标签</span>
          </div>
        </div>

        <!-- 发送至 -->
        <div class="row-item">
          <div class="row-header">
            <span class="row-label">发送至</span>
            <span class="add-btn" @click="onSelectReceivers">
              <i class="el-icon-plus"></i> 添加
            </span>
          </div>
          <div v-if="selectedReceivers.length > 0" class="selected-tags">
            <div v-for="friend in selectedReceivers" :key="friend.id" class="tag-chip">
              <span>{{ friend.showNickName || friend.nickName }}</span>
              <i class="el-icon-close" @click="onRemoveReceiver(friend.id)"></i>
            </div>
          </div>
          <div v-else class="empty-tip-row">
            <span>未选择发送对象</span>
          </div>
        </div>

        <!-- 排除 -->
        <div class="row-item">
          <div class="row-header">
            <span class="row-label">排除</span>
            <span class="add-btn" @click="onSelectExcludes">
              <i class="el-icon-plus"></i> 添加
            </span>
          </div>
          <div v-if="excludedFriends.length > 0" class="selected-tags">
            <div v-for="friend in excludedFriends" :key="friend.id" class="tag-chip exclude">
              <span>{{ friend.showNickName || friend.nickName }}</span>
              <i class="el-icon-close" @click="onRemoveExclude(friend.id)"></i>
            </div>
          </div>
          <div v-else class="empty-tip-row">
            <span>未设置排除</span>
          </div>
        </div>

        <!-- 实际发送人数 -->
        <div class="summary-row">
          <span class="summary-text">实际发送：{{ actualReceiverCount }} 人</span>
        </div>
      </div>

      <!-- 发送按钮 -->
      <div class="action-bar">
        <el-button type="primary" size="medium" :loading="sending" @click="onSend" :disabled="actualReceiverCount === 0">
          <i class="el-icon-s-promotion" v-if="!sending"></i>
          {{ sending ? '发送中...' : '发送消息' }}
        </el-button>
      </div>
    </div>

    <!-- 标签选择弹窗 -->
    <el-dialog
      title="选择标签"
      :visible.sync="tagDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="dialog-content">
        <div v-if="filterTags.length === 0" class="empty-tags">
          <span>暂无标签</span>
        </div>
        <div v-else class="tag-list">
          <div 
            v-for="tag in filterTags" 
            :key="tag.id" 
            class="tag-item"
            :class="{ selected: isTagSelected(tag) }"
            @click="onToggleTag(tag)"
          >
            <el-checkbox :value="isTagSelected(tag)" @change="() => onToggleTag(tag)"></el-checkbox>
            <div class="tag-info">
              <div class="tag-name">{{ tag.name }}</div>
              <div class="tag-count">{{ tag.memberCount }} 人</div>
            </div>
          </div>
        </div>
      </div>
      <div class="dialog-footer">
        <span class="selected-count">已选择 {{ tempSelectedTags.length }} 个标签</span>
        <el-button type="primary" @click="onConfirmTags" :disabled="tempSelectedTags.length === 0">
          确 定
        </el-button>
      </div>
    </el-dialog>

    <!-- 好友选择弹窗 -->
    <el-dialog
      :title="selectDialogTitle"
      :visible.sync="friendDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="dialog-content">
        <!-- 搜索框 -->
        <div class="search-box">
          <el-input
            v-model="searchKey"
            placeholder="搜索员工"
            prefix-icon="el-icon-search"
            clearable
          ></el-input>
        </div>
        
        <!-- 好友列表 -->
        <div class="friend-list" v-if="filteredFriends.length > 0">
          <div 
            v-for="friend in filteredFriends" 
            :key="friend.id" 
            class="friend-item"
            :class="{ selected: isFriendSelected(friend) }"
            @click="onToggleFriend(friend)"
          >
            <el-checkbox :value="isFriendSelected(friend)" @change="() => onToggleFriend(friend)"></el-checkbox>
            <head-image 
              :name="friend.showNickName || friend.nickName" 
              :url="friend.headImage" 
              :size="40"
            ></head-image>
            <div class="friend-info">
              <div class="friend-name">{{ friend.showNickName || friend.nickName }}</div>
              <div class="friend-company">{{ friend.companyName || '' }}</div>
            </div>
          </div>
        </div>
        
        <div v-else class="empty-friends">
          <span>{{ searchKey ? '未找到匹配的员工' : '暂无可选择的员工' }}</span>
        </div>
      </div>
      
      <div class="dialog-footer">
        <span class="selected-count">已选择 {{ tempSelectedFriends.length }} 人</span>
        <el-button type="primary" @click="onConfirmFriends" :disabled="tempSelectedFriends.length === 0">
          确 定
        </el-button>
      </div>
    </el-dialog>

    <!-- 发送进度弹窗 -->
    <el-dialog
      title="正在发送..."
      :visible.sync="progressDialogVisible"
      width="400px"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <div class="progress-content">
        <el-progress :percentage="progressPercentage" :stroke-width="12"></el-progress>
        <div class="progress-text">
          已发送 {{ sentCount }} / {{ totalCount }} 人
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
import useUserStore from '../../store/userStore.js';
import useChatStore from '../../store/chatStore.js';

export default {
  name: "MassMessage",
  components: {
    HeadImage
  },
  data() {
    return {
      messageContent: '',
      images: [],
      tags: [],
      selectedTags: [],
      selectedReceivers: [],
      excludedFriends: [],
      friends: [],
      
      tagDialogVisible: false,
      friendDialogVisible: false,
      progressDialogVisible: false,
      selectDialogTitle: '选择发送至',
      selectMode: 'receivers', // receivers | excludes
      
      tempSelectedTags: [],
      tempSelectedFriends: [],
      searchKey: '',
      
      totalCount: 0,
      sentCount: 0,
      sending: false
    }
  },
  computed: {
    actualReceiverCount() {
      return this.previewReceivers.length;
    },
    previewReceivers() {
      const allReceivers = new Map();
      const friendMap = new Map(this.friends.map(f => [String(f.id), f]));
      const excludedIds = new Set(this.excludedFriends.map(f => String(f.id)));
      
      // 标签内的成员
      for (const tag of this.selectedTags) {
        if (tag.memberDetails) {
          tag.memberDetails.forEach(member => {
            const memberId = String(member.id);
            if (!excludedIds.has(memberId) && !allReceivers.has(memberId)) {
              allReceivers.set(memberId, member);
            }
          });
        } else if (tag.memberIds) {
          tag.memberIds.forEach(id => {
            const memberId = String(id);
            if (!excludedIds.has(memberId) && !allReceivers.has(memberId)) {
              const friend = friendMap.get(memberId);
              allReceivers.set(memberId, friend || { id: memberId, nickName: '未知' });
            }
          });
        }
      }
      
      // 手动选择的接收人
      this.selectedReceivers.forEach(f => {
        const fId = String(f.id);
        if (!excludedIds.has(fId)) {
          allReceivers.set(fId, f);
        }
      });
      
      return Array.from(allReceivers.values());
    },
    filterTags() {
      return this.tags.filter(tag => tag.memberCount > 0);
    },
    filteredFriends() {
      if (!this.searchKey) {
        return this.friends;
      }
      const key = this.searchKey.toLowerCase();
      return this.friends.filter(f => {
        const name = (f.showNickName || f.nickName || '').toLowerCase();
        const company = (f.companyName || '').toLowerCase();
        return name.includes(key) || company.includes(key);
      });
    },
    progressPercentage() {
      if (this.totalCount === 0) return 0;
      return Math.round((this.sentCount / this.totalCount) * 100);
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    getImageUrl(dataURL) {
      // 从 dataURL 中提取纯 URL 部分（去掉 filename 参数）
      const parts = dataURL.split('&filename=');
      return parts[0];
    },
    init() {
      this.loadTags();
      this.loadFriends();
    },
    loadTags() {
      this.$http({
        url: '/friend/tag/list',
        method: 'GET'
      }).then(data => {
        this.tags = data || [];
      });
    },
    loadFriends() {
      this.$http({
        url: '/friend/list',
        method: 'GET'
      }).then(data => {
        const seen = new Set();
        this.friends = (data || []).filter(f => {
          if (seen.has(f.id)) return false;
          seen.add(f.id);
          return true;
        });
      });
    },
    isTagSelected(tag) {
      return this.tempSelectedTags.some(t => t.id === tag.id);
    },
    isFriendSelected(friend) {
      return this.tempSelectedFriends.some(f => f.id === friend.id);
    },
    onSelectTags() {
      this.tempSelectedTags = [...this.selectedTags];
      this.tagDialogVisible = true;
    },
    onToggleTag(tag) {
      const idx = this.tempSelectedTags.findIndex(t => t.id === tag.id);
      if (idx > -1) {
        this.tempSelectedTags.splice(idx, 1);
      } else {
        this.tempSelectedTags.push(tag);
      }
    },
    onConfirmTags() {
      // 获取每个标签的完整成员信息
      const promises = this.tempSelectedTags.map(tag => {
        return this.$http({
          url: `/friend/tag/detail/${tag.id}`,
          method: 'get'
        }).then(detail => {
          tag.memberDetails = (detail.members || []).map(m => ({
            id: m.friendId,
            nickName: m.nickName,
            showNickName: m.showNickName,
            headImage: m.headImage,
            companyName: m.companyName
          }));
          return tag;
        }).catch(() => tag);
      });
      
      Promise.all(promises).then(tagsWithMembers => {
        // 合并标签，保留已有的详情
        const existingIds = new Set(this.selectedTags.map(t => t.id));
        tagsWithMembers.forEach(tag => {
          if (!existingIds.has(tag.id) || !tag.memberDetails) {
            const existing = this.selectedTags.find(t => t.id === tag.id);
            if (existing && existing.memberDetails) {
              tag.memberDetails = existing.memberDetails;
            }
          }
        });
        
        const newTags = tagsWithMembers.filter(t => !existingIds.has(t.id));
        const updatedTags = this.selectedTags.map(t => {
          const updated = tagsWithMembers.find(u => u.id === t.id);
          return updated || t;
        });
        
        this.selectedTags = [...updatedTags.filter(t => tagsWithMembers.some(u => u.id === t.id)), ...newTags];
        if (this.tempSelectedTags.length > this.selectedTags.length) {
          const ids = new Set(this.selectedTags.map(t => t.id));
          this.selectedTags = [...this.selectedTags, ...tagsWithMembers.filter(t => !ids.has(t.id))];
        }
        
        this.tagDialogVisible = false;
      });
    },
    onRemoveTag(tagId) {
      const idx = this.selectedTags.findIndex(t => t.id === tagId);
      if (idx > -1) {
        this.selectedTags.splice(idx, 1);
      }
    },
    onSelectReceivers() {
      this.selectDialogTitle = '选择发送至';
      this.selectMode = 'receivers';
      this.tempSelectedFriends = [...this.selectedReceivers];
      this.searchKey = '';
      this.friendDialogVisible = true;
    },
    onSelectExcludes() {
      this.selectDialogTitle = '选择排除';
      this.selectMode = 'excludes';
      this.tempSelectedFriends = [...this.excludedFriends];
      this.searchKey = '';
      this.friendDialogVisible = true;
    },
    onToggleFriend(friend) {
      const idx = this.tempSelectedFriends.findIndex(f => f.id === friend.id);
      if (idx > -1) {
        this.tempSelectedFriends.splice(idx, 1);
      } else {
        this.tempSelectedFriends.push(friend);
      }
    },
    onConfirmFriends() {
      if (this.selectMode === 'receivers') {
        // 合并好友
        const existingIds = new Set(this.selectedReceivers.map(f => f.id));
        this.tempSelectedFriends.forEach(friend => {
          if (!existingIds.has(friend.id)) {
            this.selectedReceivers.push(friend);
          }
        });
      } else {
        // 合并排除列表
        const existingIds = new Set(this.excludedFriends.map(f => f.id));
        this.tempSelectedFriends.forEach(friend => {
          if (!existingIds.has(friend.id)) {
            this.excludedFriends.push(friend);
          }
        });
      }
      this.friendDialogVisible = false;
    },
    onRemoveReceiver(receiverId) {
      const idx = this.selectedReceivers.findIndex(f => f.id === receiverId);
      if (idx > -1) {
        this.selectedReceivers.splice(idx, 1);
      }
    },
    onRemoveExclude(excludeId) {
      const idx = this.excludedFriends.findIndex(f => f.id === excludeId);
      if (idx > -1) {
        this.excludedFriends.splice(idx, 1);
      }
    },
    onChooseImage() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp';
      input.multiple = 'multiple';
      input.onchange = (e) => {
        const files = e.target.files;
        if (files.length > 0) {
          const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
          const remaining = 9 - this.images.length;
          for (let i = 0; i < Math.min(files.length, remaining); i++) {
            const file = files[i];
            if (!allowedTypes.includes(file.type)) {
              this.$message.warning(`${file.name} 格式不支持，请选择 jpg、png、gif、webp 或 bmp 格式`);
              continue;
            }
            if (file.size > 10 * 1024 * 1024) {
              this.$message.warning(`${file.name} 超过10MB，将被跳过`);
              continue;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
              this.images.push(e.target.result);
            };
            reader.readAsDataURL(file);
          }
        }
      };
      input.click();
    },
    onRemoveImage(idx) {
      this.images.splice(idx, 1);
    },
    onSend() {
      const receivers = this.previewReceivers;
      
      if (receivers.length === 0) {
        this.$message.warning('没有可发送的员工');
        return;
      }
      
      if (!this.messageContent.trim() && this.images.length === 0) {
        this.$message.warning('请输入内容或添加图片');
        return;
      }
      
      this.$confirm(`确定发送给 ${receivers.length} 名员工？`, '确认发送', {
        confirmButtonText: '确定发送',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await this.doSend(receivers);
      }).catch(() => {});
    },
    async doSend(receivers) {
      console.log('========== doSend 开始 ==========');
      this.totalCount = receivers.length;
      this.sentCount = 0;
      this.progressDialogVisible = true;
      this.sending = true;
      
      try {
        console.log('开始上传图片，images 数组:', this.images);
        const uploadedImages = await this.uploadImages();
        console.log('图片上传结果:', uploadedImages);
        const userStore = useUserStore();
        const mine = userStore.userInfo;
        
        for (const receiver of receivers) {
          try {
            const chatInfo = {
              type: 'PRIVATE',
              targetId: receiver.id,
              showName: receiver.showNickName || receiver.nickName,
              headImage: receiver.headImage
            };
            
            const chatStore = useChatStore();
            chatStore.openChat(chatInfo);
            
            // 发送图片消息
            for (const imgItem of uploadedImages) {
              // imgItem.localPreview 是本地预览 URL，imgItem.serverData 是 UploadImageVO
              const previewData = {
                originUrl: imgItem.localPreview,
                thumbUrl: imgItem.localPreview
              };
              const tmpMessage = {
                tmpId: 'tmp_' + new Date().getTime() + '_' + Math.random(),
                sendId: mine.id,
                recvId: receiver.id,
                content: JSON.stringify(previewData),
                type: 1,
                sendTime: new Date().getTime(),
                selfSend: true,
                readedCount: 0,
                status: 1
              };
              chatStore.insertMessage(tmpMessage, chatInfo);
              
              try {
                console.log('调用 sendImageMessage，recvId:', receiver.id, 'imgData:', imgItem.serverData);
                const result = await this.sendImageMessage(receiver.id, imgItem.serverData);
                console.log('sendImageMessage 返回:', result);
                // 发送成功后，更新消息内容为服务器的 UploadImageVO
                tmpMessage.id = result.id;
                tmpMessage.content = result.content;  // 服务器返回的 content
                tmpMessage.status = 2;
                chatStore.updateMessage(tmpMessage, chatInfo);
              } catch (e) {
                console.error('发送图片失败', receiver.id, e);
              }
            }
            
            // 发送文本消息
            if (this.messageContent && this.messageContent.trim()) {
              const tmpMessage = {
                tmpId: 'tmp_' + new Date().getTime() + '_' + Math.random(),
                sendId: mine.id,
                recvId: receiver.id,
                content: this.messageContent.trim(),
                type: 0,
                sendTime: new Date().getTime(),
                selfSend: true,
                readedCount: 0,
                status: 1
              };
              chatStore.insertMessage(tmpMessage, chatInfo);
              
              try {
                const result = await this.sendTextMessage(receiver.id, this.messageContent.trim());
                tmpMessage.id = result.id;
                tmpMessage.status = 2;
                chatStore.updateMessage(tmpMessage, chatInfo);
              } catch (e) {
                console.error('发送文本失败', receiver.id, e);
              }
            }
          } catch (e) {
            console.error('发送消息失败', receiver.id, e);
          }
          
          this.sentCount++;
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        this.progressDialogVisible = false;
        this.$message.success('发送完成');
        
      } catch (e) {
        console.error('========== doSend 捕获到错误 ==========', e);
        this.progressDialogVisible = false;
        this.$message.error('发送失败: ' + (e.message || '未知错误'));
      } finally {
        this.sending = false;
      }
    },
    async uploadImages() {
      const uploadedImages = [];
      for (let i = 0; i < this.images.length; i++) {
        try {
          console.log('开始上传图片，当前 imgData 类型:', typeof this.images[i]);
          const serverData = await this.uploadSingleImage(this.images[i]);
          console.log('图片上传成功，返回的 serverData:', serverData);
          uploadedImages.push({
            localPreview: this.images[i],
            serverData: serverData
          });
        } catch (e) {
          console.error('上传图片失败', e);
        }
      }
      console.log('所有图片上传完成，uploadedImages:', uploadedImages);
      return uploadedImages;
    },
    uploadSingleImage(fileData) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        const blobAndName = this.dataURLtoBlobWithName(fileData);
        formData.append('file', blobAndName.blob, blobAndName.filename);
        
        this.$http({
          url: '/image/upload?isPermanent=false&thumbSize=50',
          method: 'post',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(data => {
          // 返回完整的 UploadImageVO 对象
          resolve(data);
        }).catch(err => {
          reject(err);
        });
      });
    },
    dataURLtoBlobWithName(dataURL) {
      const arr = dataURL.split(',');
      const meta = arr[0].match(/:(.*?);/);
      const mime = meta ? meta[1] : 'image/png';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      // 从 MIME 类型生成正确的文件扩展名
      const extMap = {
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'image/bmp': 'bmp'
      };
      const ext = extMap[mime] || 'png';
      const filename = 'image_' + Date.now() + '.' + ext;
      const blob = new Blob([u8arr], { type: mime });
      return { blob, filename };
    },
    dataURLtoBlob(dataURL) {
      const arr = dataURL.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    },
    sendImageMessage(recvId, imgData) {
      console.log('sendImageMessage 被调用，recvId:', recvId, 'imgData:', imgData);
      // imgData 是 UploadImageVO 对象，content 应该是 JSON 字符串
      const content = JSON.stringify(imgData);
      console.log('发送内容:', content);
      return this.$http({
        url: '/message/private/send',
        method: 'post',
        data: {
          recvId: recvId,
          content: content,
          type: 1
        }
      }).then(res => {
        console.log('消息发送成功，返回:', res);
        return res;
      }).catch(err => {
        console.error('消息发送失败:', err);
        throw err;
      });
    },
    sendTextMessage(recvId, content) {
      return this.$http({
        url: '/message/private/send',
        method: 'post',
        data: {
          recvId: recvId,
          content: content,
          type: 0
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.mass-message {
  padding: 15px;
  background: #fafbfc;
  min-height: 400px;

  .section-header {
    margin-bottom: 20px;

    .section-title {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--im-text-color-primary);

      i {
        color: var(--im-color-primary);
        font-size: 18px;
      }
    }
  }

  .content-wrapper {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;

    .section-label {
      font-size: 14px;
      color: #333;
      font-weight: 500;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 8px;

      .image-hint {
        font-size: 12px;
        color: #999;
        font-weight: normal;
      }
    }
  }

  .message-section {
    margin-bottom: 20px;

    :deep(.el-textarea__inner) {
      resize: none;
    }
  }

  .image-section {
    margin-bottom: 20px;

    .image-list {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;

      .image-item {
        width: 100px;
        height: 100px;
        border-radius: 8px;
        overflow: hidden;
        position: relative;

        .preview-img {
          width: 100%;
          height: 100%;
        }

        .image-error {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f5f5;
          color: #999;
          font-size: 24px;
        }

        .remove-img {
          position: absolute;
          top: 4px;
          right: 4px;
          width: 24px;
          height: 24px;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;

          i {
            color: white;
            font-size: 12px;
          }
        }
      }

      .add-image-btn {
        width: 100px;
        height: 100px;
        border: 1px dashed #ddd;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          border-color: var(--im-color-primary);
          color: var(--im-color-primary);
        }

        i {
          font-size: 28px;
          color: #999;
        }
      }
    }
  }

  .range-section {
    .row-item {
      margin-bottom: 16px;

      &:last-of-type {
        margin-bottom: 0;
      }

      .row-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 10px;

        .row-label {
          font-size: 14px;
          color: #333;
        }

        .add-btn {
          font-size: 13px;
          color: var(--im-color-primary);
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;

          &:hover {
            opacity: 0.8;
          }
        }
      }

      .selected-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .empty-tip-row {
        color: #999;
        font-size: 13px;
        padding: 8px 0;
      }
    }

    .summary-row {
      padding-top: 16px;
      border-top: 1px solid #f0f0f0;
      margin-top: 16px;

      .summary-text {
        font-size: 14px;
        color: var(--im-color-primary);
        font-weight: 500;
      }
    }
  }

  .tag-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #e8f4ff;
    border-radius: 16px;
    font-size: 13px;
    color: var(--im-color-primary);

    i {
      font-size: 10px;
      cursor: pointer;

      &:hover {
        opacity: 0.7;
      }
    }

    &.exclude {
      background: #fff0f0;
      color: #e54d42;
    }
  }

  .action-bar {
    margin-top: 24px;
    text-align: center;

    :deep(.el-button) {
      padding: 12px 40px;
    }
  }

  .dialog-content {
    .empty-tags {
      text-align: center;
      padding: 40px 0;
      color: #999;
    }

    .tag-list {
      max-height: 400px;
      overflow-y: auto;

      .tag-item {
        display: flex;
        align-items: center;
        padding: 12px;
        border-radius: 8px;
        cursor: pointer;
        transition: background 0.2s;

        &:hover {
          background: #f5f5f5;
        }

        &.selected {
          background: #f0f7ff;
        }

        .el-checkbox {
          margin-right: 12px;
        }

        .tag-info {
          flex: 1;

          .tag-name {
            font-size: 14px;
            color: #333;
          }

          .tag-count {
            font-size: 12px;
            color: #999;
            margin-top: 2px;
          }
        }
      }
    }

    .search-box {
      margin-bottom: 16px;
    }

    .friend-list {
      max-height: 350px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 8px;

      .friend-item {
        display: flex;
        align-items: center;
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        transition: background 0.2s;

        &:hover {
          background: #f5f5f5;
        }

        &.selected {
          background: #f0f7ff;
        }

        .el-checkbox {
          margin-right: 12px;
        }

        .friend-info {
          flex: 1;
          margin-left: 12px;

          .friend-name {
            font-size: 14px;
            color: #333;
          }

          .friend-company {
            font-size: 12px;
            color: #999;
            margin-top: 2px;
          }
        }
      }
    }

    .empty-friends {
      text-align: center;
      padding: 40px 0;
      color: #999;
    }
  }

  .dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;

    .selected-count {
      font-size: 14px;
      color: #666;
    }
  }

  .progress-content {
    .progress-text {
      text-align: center;
      margin-top: 16px;
      font-size: 14px;
      color: #666;
    }
  }
}
</style>
